<template>
	<DexDataTable
		ref="documentList"
		:key="tableKey"
		class="p-shadow-2 p-datatable-documents"
		:value="documents"
		:lazy="true"
		:paginator="true"
		:loading="!loaded || lockTable"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:rows="paginationInfo.pageSize"
		:totalRecords="paginationInfo.total"
		columnResizeMode="expand"
		responsiveLayout="scroll"
		:scrollable="true"
		scrollHeight="flex"
		v-model:selection="selection"
		:rowHover="true"
		@row-click="onRowClick($event)"
		@page="onPage($event)"
		@filter="onFilter($event.filters)"
		v-model:filters="filters"
		filterDisplay="row"
		sortMode="multiple"
		@sort="onSort($event)"
		:multiSortMeta="currentSortMeta"
		:removableSort="true"
		:first="(paginationInfo.page * paginationInfo.pageSize)"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords} '"
		:metaKeySelection="false"
		:resizableColumns="true"
		@column-resize-end="columnResize"
		:fieldColumns="fieldColumns"
	>
		<template #loading>
			{{$t('Squeeze.Queue.Filter.Loading')}}
		</template>
		<template #empty>
			{{$t('Squeeze.Queue.Filter.NoEntries')}}
		</template>
		<Column v-if="store.state.scopes.sqzRequeue && ($route.name === 'DocumentListValidation' || $route.name === 'DocumentListValidationWithSearch')" selectionMode="multiple" headerClass="header-center not-resizable-column" style="min-width: 4rem; max-width: 4rem; text-align: center;" :notResizableColumn="true" />
		<Column field="id" :header="$t('Squeeze.Queue.Fields.id')" :style="getBaseColumnWidth('id', 6)" :showFilterMenu="false" :sortable="true">
			<template #filter="{filterModel,filterCallback}">
				<InputText style="min-width: 3rem;" class="filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" placeholder=""/>
			</template>
		</Column>
		<Column v-if="$route.name !== 'DocumentListValidation' && $route.name !== 'DocumentListValidationWithSearch'" field="step" :header="$t('Squeeze.Queue.Fields.Step')" :style="getBaseColumnWidth('step', 12)" :frozen="frozenColumns.step" filterField="queueStep" :showFilterMenu="false">
			<template #header>
				<i :class="frozenColumns.step ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 3.2rem;"
					class="frozenIcon"
					@click="setFrozenColumns('step')"
				></i>
			</template>
			<template #body="document">
				<div style="text-align: left!important; white-space:nowrap!important;">
					<template v-if="document.data.workflowContext.step !== 'validation' && document.data.exports && document.data.exports.find(exportData => exportData.interfaceName === 'EAS')">
						{{$t("Squeeze.Queue.StepsList.ARCHIVE")}} <i class="mdi mdi-archive-outline" v-tooltip="getExportsForToolTip(document.data.exports)"></i>
					</template>
					<template v-else>
						{{$t("Squeeze.Queue.StepsList." + document.data.workflowContext.step.toUpperCase())}}
						<template v-if="document.data.exports && document.data.exports.length > 0">
							<i class="mdi mdi-archive-outline" v-tooltip="getExportsForToolTip(document.data.exports)"></i>
						</template>
					</template>
				</div>
			</template>
			<template #filter="{filterModel,filterCallback}">
				<Dropdown v-model="filterModel.value" @change="filterCallback()" :options="steps" placeholder="" class="p-column-filter filter-field" :showFilterMenu="false" :showClear="true" style="height: 2.16rem; min-width: 9rem;">
					<template #value="document">
						<span v-if="document.value">{{$t("Squeeze.Queue.StepsList." + document.value.toUpperCase())}}</span>
						<span v-else>{{document.placeholder}}</span>
					</template>
					<template #option="document">
						{{$t("Squeeze.Queue.Steps." + document.option)}}
					</template>
				</Dropdown>
			</template>
		</Column>
		<Column field="status" :header="$t('Squeeze.Queue.Fields.status')" :style="getBaseColumnWidth('status', 12)" :frozen="frozenColumns.status" filterField="queueStatus" :showFilterMenu="false" :sortable="true">
			<template #header>
				<i :class="frozenColumns.status ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 4.7rem;"
					class="frozenIcon"
					@click="setFrozenColumns('status')"
				></i>
			</template>
			<template #body="document">
				<div style="text-align: left!important; white-space:nowrap!important;">
					<span :class="'status-badge ' + document.data.workflowContext.status.toLowerCase()" class="p-py-1">
						{{$t("Squeeze.Queue.States." + document.data.workflowContext.status)}}
					</span>
				</div>
			</template>
			<template #filter="{filterModel,filterCallback}">
				<Dropdown v-model="filterModel.value" @change="filterCallback()" :options="statuses" placeholder="" class="p-column-filter filter-field" :showClear="true" style="height: 2.16rem; min-width: 9rem;">
					<template #value="document">
						<span v-if="document.value">{{$t("Squeeze.Queue.States." + document.value)}}</span>
						<span v-else>{{document.placeholder}}</span>
					</template>
					<template #option="document">
						<span :class="'status-badge ' + document.option.toLowerCase()">{{$t("Squeeze.Queue.States." + document.option)}}</span>
					</template>
				</Dropdown>
			</template>
		</Column>

		<Column field="create_ts" filterField="queueCreateTs" :header="$t('Squeeze.WorkflowContext.createdTs')" :style="getBaseColumnWidth('create_ts', 14)" :showFilterMenu="false" :sortable="true">
			<template #body="document">
				<div class="column-cell" v-tooltip.bottom="formatDate(document.data.createdAt)" style="white-space:nowrap!important;">
					{{formatDate(document.data.createdAt)}}
				</div>
			</template>
			<template #filter="{filterModel,filterCallback}">
				<IconField>
					<InputIcon v-if="showDocumentationDialog && currentFilterFieldName === 'queueCreateTs'" class="mdi mdi-comment-question-outline" style="cursor: pointer;" @click="toggleOverlay($event)" />
					<InputText style="min-width: 11rem;" class="filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" placeholder="" @focus="onClickFilterFieldHelpIcon('queueCreateTs', 'date')" />
				</IconField>
			</template>
		</Column>

		<Column field="errorText" :header="$t('Squeeze.General.Comment')" :style="getBaseColumnWidth('errorText', 14)" style="overflow: hidden;" :frozen="frozenColumns.errorText" filterField="queueErrorText" :showFilterMenu="false" :sortable="true">
			<template #header>
				<i :class="frozenColumns.errorText ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 7rem;"
					class="frozenIcon"
					@click="setFrozenColumns('errorText')"
				></i>
			</template>
			<template #body="document">
				<div class="column-cell" v-tooltip.bottom="document.data.workflowContext.errorText">
					{{document.data.workflowContext.errorText}}
				</div>
			</template>
			<template #filter="{filterModel,filterCallback}">
				<IconField>
					<InputIcon v-if="showDocumentationDialog && currentFilterFieldName === 'queueErrorText'" class="mdi mdi-comment-question-outline" style="cursor: pointer;" @click="toggleOverlay($event)" />
					<InputText style="min-width: 11rem;" class="filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" placeholder="" @focus="onClickFilterFieldHelpIcon('queueErrorText', 'text')" />
				</IconField>
			</template>
		</Column>

		<Column v-for="column of fieldColumns"
				:field="column.name" :filterField="'' + column.id" :header="column.header"
				:key="column.id"
				:style="column.type.toLowerCase() === 'amount'
					? 'min-width: 15rem; max-width: 15rem; overflow: hidden;'
					: (column.colWidth
						? 'min-width:' + column.colWidth + 'rem; max-width:' + column.colWidth + 'rem; overflow: hidden;'
						: 'min-width: 17rem; max-width: 17rem; overflow: hidden;')"
				:showFilterMenu="true"
				:sortable="true"
		>
			<template #body="document">
				<div :style="column.type.toLowerCase() === 'amount' ? 'text-align:right!important;' : 'text-align:left;'" class="column-cell" v-tooltip.bottom="document.data.fields['' + column.id] && document.data.fields['' + column.id].value ? formatValue(document.data.fields['' + column.id].value, column.type) : ''">
					<template v-if="document.data.fields['' + column.id] && document.data.fields['' + column.id].value">
						{{formatValue(document.data.fields['' + column.id].value, column.type)}}
					</template>
				</div>
			</template>
			<template #filter="{filterModel,filterCallback}">
				<IconField>
					<InputIcon v-if="showDocumentationDialog && currentFilterFieldName === column.name" class="mdi mdi-comment-question-outline" style="cursor: pointer;" @click="toggleOverlay($event)" />
					<InputText :style="column.type.toLowerCase() === 'amount' ? 'min-width: 12rem;' : 'min-width: 14rem;'" class="filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" placeholder="" @focus="onClickFilterFieldHelpIcon(column.name, column.type)"/>
				</IconField>
			</template>
		</Column>

		<template #paginatorstart>
			<Button type="button" v-tooltip="$t('Squeeze.General.Refresh')" icon="mdi mdi-refresh" class="p-button-text p-ml-r" @click="onReload" :disabled="!loaded" />
			<Button type="button" v-tooltip="$t('Squeeze.General.ClearSearch')" icon="mdi mdi-filter-variant-remove" class="p-button-text p-ml-r p-mr-2" @click="clearSearch" :disabled="!loaded" />
			<InputText style="width: auto;" class="filter-field" type="text" :value="fullText" v-on:input="emit('update:fullText', $event.target.value)" @keydown.enter="onReload" :placeholder="$t('Squeeze.General.Search') + '...'"/>
			<Button v-if="store.state.featureSet.savedDocumentSearches" type="button" v-tooltip="$t('Squeeze.General.SaveSearchList')" icon="mdi mdi-filter-plus" class="p-button-text p-ml-r p-ml-2" @click="showSaveSearch" :disabled="!loaded" />
			<Button v-if="store.state.featureSet.savedDocumentSearches && showSelectSearch" type="button" v-tooltip="$t('Squeeze.General.OpenSearch')" icon="mdi mdi-filter-menu" class="p-button-text p-ml-r" @click="openSelectSearch" :disabled="!loaded" />
			<Button v-if="store.state.scopes.sqzRequeue && ($route.name === 'DocumentListValidation' || $route.name === 'DocumentListValidationWithSearch')" type="button" icon="mdi mdi-file-refresh-outline" class="p-button-text" :label="$t('Squeeze.Queue.Requeue.Requeue')" @click="openMultiRequeueMenu" :disabled="!selection.length" aria-haspopup="true" aria-controls="multi_entry_menu" />
			<Menu v-if="store.state.scopes.sqzRequeue &&  ($route.name === 'DocumentListValidation' || $route.name === 'DocumentListValidationWithSearch')" id="multi_entry_menu" ref="multiEntryMenu" :model="queueEntrySteps" :popup="true" />
		</template>
		<template #paginatorend>
			<Button type="button" v-tooltip="$t('Squeeze.Validation.Buttons.ResetTableSettings')" icon="mdi mdi-sort-variant-remove" class="p-button-text" @click="resetTableSettings" :disabled="!loaded" />
			<Button type="button" v-tooltip="$t('Squeeze.Validation.Buttons.ResetColumnWidths')" icon="mdi mdi-table-column-width" class="p-button-text" @click="resetColumnWidths" :disabled="!loaded" />
		</template>
	</DexDataTable>
	<EntryDialog
		:show="showDelete"
		@onClose="showDelete = false, isValidationInvalid = true, showValidationMessage = false"
		@onConfirm="requeueSelectedEntries('Delete', comment)"
		:loading="!loaded || lockTable"
		:headerText="deleteHeaderText"
		:saveButtonText="$t('Squeeze.General.Delete')"
	>
		<template #content>
			<BlockUI :blocked="false">
				<QueueDeleteForm
					:queueEntries="selection"
					:showErrorMessage="showValidationMessage"
					@update="onUpdate"
				/>
			</BlockUI>
		</template>
	</EntryDialog>

	<OverlayPanel ref="overlayFunctionalityList">
		<h4>{{ $t('Squeeze.Queue.InfoSearchOptions') }}</h4>
		<HelpFunctionalityList :currentFilterFieldType="currentFilterFieldType" />
	</OverlayPanel>
</template>

<script lang="ts">
/* eslint max-lines: off */
import DexDataTable from "@/components/datatable/DexDataTable.vue";
import Column from 'primevue/column';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import { i18n } from "@/main";
import {formatDate as formatDateUtil, getLocaleCode} from "@/util/DateHelper";
import {ListDocument} from "@/apps/squeeze/interfaces/ListDocument";
import {DocumentFilterObject, DocumentSortObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import {ExportStatus} from "@dex/squeeze-client-ts/dist/api";
import Tooltip from "primevue/tooltip";
import {PaginationDto, QueueEntry} from "@dex/squeeze-client-ts";
import {useSqueezeStore} from "@/apps/squeeze/store";
import { MenuItem } from '@/shims-prime-vue';
import {useConfirm} from "primevue/useconfirm";
import {computed, defineComponent, onMounted, PropType, reactive, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import { getSqueezeStepIconMap } from "@/apps/squeeze/App.vue";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import {ClientManager} from "@/singletons/ClientManager";
import Menu from "primevue/menu";
import EntryDialog from "@/components/EntryDialog.vue";
import BlockUI from "primevue/blockui";
import QueueDeleteForm from "@/apps/administration/components/queue/QueueDeleteForm.vue";
import HelpFunctionalityList from "@/apps/squeeze/components/HelpFunctionalityList.vue";
import OverlayPanel from "primevue/overlaypanel";
import {FieldColumn} from "@/apps/squeeze/interfaces/FieldColumn";
import {TableSettings} from "@/util/TableSettings";
import {ParsingHelper} from "@/util/ParsingHelper";
import {useRoute} from "vue-router";

interface FrozenColumns {
	step: boolean;
	status: boolean;
	errorText: boolean;
}

interface ColumnWidth extends FieldColumn {
	colWidth?: string;
	minWidth?: string;
}


/**
 * @description Uses Elastic Search API endpoint instead of Queue API endpoint
 */
export default defineComponent({
	name: "DocumentList",
	components: {
		OverlayPanel,
		DexDataTable,
		Column,
		IconField,
		InputIcon,
		InputText,
		Dropdown,
		Menu,
		EntryDialog,
		BlockUI,
		QueueDeleteForm,
		HelpFunctionalityList,
	},
	props: {
		documentClassId: {
			type: Number,
			required: true,
		},
		documentClassName: {
			type: String,
			required: true,
		},
		documents: {
			type: Array as PropType<ListDocument[]>,
			default: () => [],
		},
		fieldColumns: {
			type: Array as PropType<ColumnWidth[]>,
			default: () => [],
		},
		loaded: {
			type: Boolean,
			default: false,
		},
		paginationInfo: {
			type: Object as PropType<PaginationDto>,
		},
		filterOptions: {
			type: Object as PropType<DocumentFilterObject>,
		},
		fullText: {
			type: String,
			default: '',
		},
		showSelectSearch: {
			type: Boolean,
			default: false,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["onRowSelect", "onReload", "onPage", "onFilter", "onSort", "update:fullText", "openSaveSearch", "openSelectSearch", "clearSearch"],
	setup(props, { emit }) {
		const {t} = useI18n();
		const route = useRoute();
		const store = useSqueezeStore();
		const toast = useToast();
		const confirm = useConfirm();

		const documentList = ref<any>();

		/** Filters of list */
		const filters = ref<DocumentFilterObject>(props.filterOptions!);

		/** QueueStates for filter selection */
		const statuses =  ref<string[]>([
			'INITIAL', 'WORK', 'WAITING', 'ERROR', 'SUSPEND', 'CLARIFICATION', 'FINISHED',
		]);

		/** QueueStates for filter selection */
		const steps =  ref<string[]>([
			'Backup', 'Delete', 'SplitDocument', 'Validation',
		]);

		/** List of allowed filters */
		const allowedFilters = computed(() => {
			return [{value: FilterMatchMode.EQUALS, label: t('Squeeze.Queue.Filter.Options.EQUALS')},
				{value: FilterMatchMode.CONTAINS, label: t('Squeeze.Queue.Filter.Options.CONTAINS')},
				{value: FilterMatchMode.STARTS_WITH, label: t('Squeeze.Queue.Filter.Options.STARTS_WITH')},
				{value: FilterMatchMode.ENDS_WITH, label: t('Squeeze.Queue.Filter.Options.ENDS_WITH')}]
		})

		/** Currently-Selected row */
		const selection = ref([]);

		/** Current locale */
		/*const lang = ref(DEFAULT_LOCALE);*/

		/** Requeue steps to choose in menu */
		const queueEntrySteps = ref<MenuItem[]>([]);

		/** Document API endpoint */
		const documentApi = ClientManager.getInstance().squeeze.document;

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Should the table be locked? */
		const lockTable = ref<boolean>(false);

		/** Ref for menu */
		const multiEntryMenu = ref(null);

		/** Is Delete Document Dialog visible? */
		const showDelete = ref<boolean>(false);

		/** Comment for delete of document */
		const comment = ref<string>('');

		/** Show error validation-messages in form? */
		const showValidationMessage = ref(false);

		/** Is the Form of the migrations invalid? */
		const isValidationInvalid = ref(true);

		/** Text (delete) of the header in Entry-Dialog */
		const deleteHeaderText = computed(() => {
			if (selection.value.length > 1) {
				return t('Squeeze.Queue.General.DeleteDocuments', { count: selection.value.length});
			}
			return t('Squeeze.Queue.General.DeleteDocument');
		});

		/** Is the Documentation Dialog visible? */
		const showDocumentationDialog = ref<boolean>(false);

		/** Current filter field name*/
		const currentFilterFieldName = ref<string>('');

		/** Current filter field type */
		const currentFilterFieldType = ref<string>('');

		/** Component of the overlay panel for the documentation of all functionalities */
		const overlayFunctionalityList = ref<any>();

		/** Which columns are frozen? */
		const frozenColumns = reactive<FrozenColumns>({
			step: true,
			status: true,
			errorText: false,
		});

		/** List of all table column widths */
		const allColumnWidths = ref<any[]>([]);

		/** Key to force re-render of the table (e.g. after column width reset) */
		const tableKey = ref<number>(0);

		/** Current sort meta for the DataTable */
		const currentSortMeta = ref<DocumentSortObject[]>([]);

		/** Set frozen columns */
		const setFrozenColumns = (column: string) => {
			(frozenColumns as any)[column] = !(frozenColumns as any)[column];
			TableSettings.saveTableSubSetting(t, toast, store, userApi, "DocumentListView", 'frozenColumns', props.documentClassId, { ...frozenColumns });
		}

		/**
		 * Toggle the overlay panel
		 * Is the overlay panel shown?
		 * @param event
		 */
		const toggleOverlay = (event: any) => {
			overlayFunctionalityList.value.toggle(event);
		}

		/**
		 * Triggered if the filter field help icon is clicked
		 * @param {string} columnName
		 * @param {string} columnType
		 */
		const onClickFilterFieldHelpIcon = (columnName: string, columnType: string) => {
			currentFilterFieldName.value = columnName;
			currentFilterFieldType.value = columnType;
			showDocumentationDialog.value = true;
		}

		/** Triggered on update of attribute-form */
		const onUpdate = (data: { comment: string }, isInvalid: boolean) => {
			isValidationInvalid.value = isInvalid;
			comment.value = data.comment;
		}

		const onFilter = (tableFilters: DocumentFilterObject) => {
			emit("onFilter", tableFilters);
		}

		/**
		 * Triggered when a row is clicked
		 * @param event
		 */
		const onRowClick = (event: { originalEvent: MouseEvent; data: QueueEntry; index: number }) => {
			/* Prevent the row-click in the selection-column with the checkbox
			* Use the selectionMode 'multiple' only at the respective column and not at the table, when you also use the row-click event
			* If the selectionMode is set on the table and the row click event is active, these two events overwrite each other
			*/
			if (event.originalEvent
				&& !(event.originalEvent.target as HTMLElement).matches('.p-selection-column')
				&& !(event.originalEvent.target as HTMLElement).matches('.p-checkbox *')) {
				emit("onRowSelect", event.data);
			}
			return;
		}

		/** Triggered when the next page is selected */
		const onPage = (event: any) => {
			emit("onPage", event)
		}

		/** Triggered on sort a column */
		const onSort = (event: { multiSortMeta: DocumentSortObject[] }) => {
			currentSortMeta.value = event.multiSortMeta ?? [];
			TableSettings.saveTableSubSetting(t, toast, store, userApi, "DocumentListView", 'sort', props.documentClassId, currentSortMeta.value, v => v.length === 0);
			emit("onSort", event.multiSortMeta)
		}

		/** Triggered when table content should be reloaded */
		const onReload = () => {
			emit("onReload");
		}

		/** Opens the dialog for saving/deleting searches */
		const showSaveSearch = () => {
			documentList.value.onFilterApply();
			emit("openSaveSearch");
		}

		/** Opens the Search Selection */
		const openSelectSearch = () => {
			emit("openSelectSearch");
		}

		/** Clears the current search */
		const clearSearch = () => {
			emit("clearSearch");
		}

		/** Formats date string to desired locale string
		 * @param dateToFormat
		 */
		const formatDate = (dateToFormat: string): string => formatDateUtil(dateToFormat, i18n.global.locale.value);

		/** Formats an amount to locale string (Formats are only defined in the backend) */
		const formatAmounts = (value: string) => {
			return ParsingHelper.formatAmount(value, getLocaleCode(i18n.global.locale.value));
		}

		/** Formats given values to data type specific formats */
		const formatValue = (value: string, dataType: string) => {
			if (!value) {
				return "";
			}

			switch(dataType.toLowerCase()) {
			case "amount": return formatAmounts(value);
			//case "date": return formatDate(value);
			default: return value;
			}
		}

		/**
		 * Creates the Tooltip for Exports
		 * @param exports
		 */
		const getExportsForToolTip = (exports: ExportStatus[]) => {
			let toolTip = "";
			exports.forEach(exportData => {
				toolTip += exportData.interfaceName + ":\n " + t("Squeeze.Queue.States." + exportData.status) + "\n " + formatDate(exportData.timeStamp!) + "\n" + exportData.result + "\n\n";
			})

			return toolTip;
		}

		/**
		 * Opens multi requeue menu
		 * @param event
		 */
		const openMultiRequeueMenu = (event: any) => {
			if (!selection.value.length) {
				return;
			}
			const multiMenu = multiEntryMenu.value as any;
			if(multiMenu) {
				multiMenu.toggle(event);
			}
		}

		/**
		 * Requeue the selected entries
		 * @param step
		 * @param comment
		 */
		const requeueSelectedEntries = (step: string, comment?: string) => {
			if (!selection.value || !selection.value.length) {
				return;
			}

			// delete dialog
			if (isValidationInvalid.value && step === 'Delete') {
				showValidationMessage.value = true;
				return;
			}

			lockTable.value = true;
			selection.value.reduce((chain, { id }) => {
				if (id == undefined) {
					return chain;
				}
				return chain.finally(() => documentApi.requeueDocument(id, step, undefined, comment ?? "").catch(() => { /* swallow errors */ }))
			}, Promise.resolve()).finally(() => {
				ToastManager.showSuccess(toast, t('Squeeze.Queue.Requeue.Success'), t('Squeeze.Queue.Steps.' + step));
				selection.value = [];
				lockTable.value = false;
				showDelete.value = false;
				onReload();
			});
		}

		/**
		 * Get base column width
		 * @param fieldName
		 * @param width
		 */
		const getBaseColumnWidth = (fieldName: string, width: number) => {
			const savedWidth = allColumnWidths.value
				.find(column => column.name === fieldName)
				?.colWidth;
			const w = savedWidth ?? width;
			// The maximum width is increased by 2 due to the function that removes the filter (icon)
			return 'width: ' + w + 'rem; min-width: ' + w + 'rem; max-width: ' + w + 'rem; overflow: hidden;';
		}

		/** Reset sort and frozen column settings */
		const resetTableSettings = () => {
			currentSortMeta.value = [];
			TableSettings.saveTableSubSetting(t, toast, store, userApi, "DocumentListView", 'sort', props.documentClassId, [], v => v.length === 0);
			emit("onSort", []);

			Object.keys(frozenColumns).forEach(key => delete (frozenColumns as any)[key]);
			Object.assign(frozenColumns, { step: true, status: true, errorText: false });
			TableSettings.saveTableSubSetting(t, toast, store, userApi, "DocumentListView", 'frozenColumns', props.documentClassId, null, () => true);
		}

		/** Reset column widths */
		const resetColumnWidths = () => {
			// reset array
			allColumnWidths.value = [];

			// delete column widths
			props.fieldColumns.forEach(field => {
				if (field.colWidth) {
					delete field.colWidth;
				}
			});

			TableSettings.saveDocumentListTableColumnWidths(t, toast, store, userApi, props.documentClassId, allColumnWidths.value);

			// force re-render to clear PrimeVue's internally set inline column widths
			tableKey.value++;
		}

		/**
		 * Get base column config
		 * @param routeName
		 */
		const getBaseColumnConfig = (routeName: string) => {
			// check route
			const isValidationView = routeName === 'DocumentListValidation' || routeName === 'DocumentListValidationWithSearch';

			// base columns of datatable
			const baseColumns = [
				{ index: 0, field: 'selection' },
				{ index: 1, field: 'id' },
				{ index: 2, field: isValidationView ? 'status' : 'step' },
				{ index: 3, field: isValidationView ? 'create_ts' : 'status' },
				{ index: 4, field: isValidationView ? 'errorText' : 'create_ts' },
			];

			// column that is only added in the non-validation view
			// in this particular case, it is also a base column.
			const optionalColumn = !isValidationView
				? [{ index: 5, field: 'errorText' }]
				: [];

			return {
				validationView: isValidationView,
				columns: [
					...baseColumns,
					...optionalColumn,
				],
			};
		};

		/** Trigger the column resize
		 * @param event
		 */
		const columnResize = (event: any) => {
			// skip if the column was not actually moved
			if (!event.delta) return;

			// convert px to rem
			const columnWidthInRem = ParsingHelper.pxToRem(event.element.offsetWidth);

			// get current column (of dynamics field columns)
			const currentColumn = props.fieldColumns.find((column: ColumnWidth) => column.header === event.element.innerText) as ColumnWidth;
			if (currentColumn && currentColumn.id && columnWidthInRem) {
				if (allColumnWidths.value && allColumnWidths.value.find((columnWidth: any) => columnWidth.id === currentColumn.id)) {
					// if a column width has already been set, it will be overwritten
					const col = allColumnWidths.value.find((columnWidth: any) => columnWidth.id === currentColumn.id);
					col.colWidth = String(columnWidthInRem);
				} else {
					// if no column width exists, it will be set
					currentColumn.colWidth = String(columnWidthInRem);
					allColumnWidths.value = [...allColumnWidths.value, currentColumn];
				}
			} else {
				// get base column
				const columnElement = event.element?.closest('th[role="columnheader"]');
				const columnIndex = columnElement?.cellIndex;

				if (columnIndex === null || columnIndex === undefined) return;

				const config = getBaseColumnConfig(route.name as string);
				const columnConfig = config.columns.find(col => col.index === columnIndex);

				if (!columnConfig) return;

				// check if column exists in allColumnWidths and set the new column width
				if (allColumnWidths.value && allColumnWidths.value.find((columnWidth: any) => columnWidth.name === columnConfig.field)) {
					const col = allColumnWidths.value.find((columnWidth: any) => columnWidth.name === columnConfig.field);
					col.colWidth = String(columnWidthInRem);
				} else {
					// set column width of a base column when not exists
					const currentColumn = {
						colWidth: String(columnWidthInRem),
						header: event.element.innerText,
						id: null,
						name: columnConfig.field,
						type: "Text",
					};

					allColumnWidths.value = [...allColumnWidths.value, currentColumn];
				}
			}

			TableSettings.saveDocumentListTableColumnWidths(t, toast, store, userApi, props.documentClassId, allColumnWidths.value);
		};

		watch(props.filterOptions!, () => {
			Object.assign(filters.value, props.filterOptions);
		})

		onMounted(() => {
			// Initialize all column widths
			if (store.state.userSettings.tableSettings.length
				&& 'documentList' in JSON.parse(store.state.userSettings.tableSettings)
				&& JSON.parse(store.state.userSettings.tableSettings).documentList[props.documentClassId]) {
				allColumnWidths.value = JSON.parse(store.state.userSettings.tableSettings).documentList[props.documentClassId];
			}

			// Initialize sort settings (UI only — data loading is triggered by the parent after fieldColumns are ready)
			const savedSort = TableSettings.getTableSubSetting<DocumentSortObject[]>(store, "DocumentListView", 'sort', props.documentClassId) ?? [];
			if (savedSort.length) {
				currentSortMeta.value = savedSort;
			}

			// Initialize frozen columns
			const savedFrozenColumns = TableSettings.getTableSubSetting<Record<string, boolean>>(store, "DocumentListView", 'frozenColumns', props.documentClassId);
			if (savedFrozenColumns) {
				Object.assign(frozenColumns, savedFrozenColumns);
			}

			queueEntrySteps.value = [{
				label: t('Squeeze.Queue.General.Steps'),
				items: [],
			}];
			// Only build menu entries for steps the backend currently reports as available
			// (already respects feature flags/license); the local icon map is used as a
			// safeguard so unknown step names from the backend are never rendered
			store.state.queueSteps
				.filter((step: string) => getSqueezeStepIconMap().has(step))
				.forEach((step: string) => {
					const icon = getSqueezeStepIconMap().get(step) ?? '';
					if (step === 'Delete') {
						queueEntrySteps.value[0].items.push({
							label: t('Squeeze.Queue.Steps.' + step),
							icon: icon,
							command: () => {
								showDelete.value = true;
							},
						})
					} else {
						queueEntrySteps.value[0].items.push({
							label: t('Squeeze.Queue.Steps.' + step),
							icon: icon,
							command: (ev: { item: any; originalEvent: MouseEvent }) => {
								let messageText = '';
								let acceptFn = null;

								if ((ev.originalEvent.target as HTMLElement).matches('#multi_entry_menu *')) {
									// If entry in multi_menu is clicked (Requeue-Button under table for multi-selection)
									messageText = t('Squeeze.Queue.Requeue.ForwardEntries');
									acceptFn = () => {
										requeueSelectedEntries(step);
									};
								}
								else {
									return;
								}

								confirm.require({
									message: messageText + ": " + t('Squeeze.Queue.Steps.' + step) + " ?",
									header: t('Squeeze.General.Confirmation'),
									icon: 'pi pi-exclamation-triangle',
									accept: acceptFn,
									reject: () => { /**/ },
								});
							},
						});
					}
				});
		})

		return {
			filters, allowedFilters, steps, statuses, store, queueEntrySteps, selection, multiEntryMenu,
			lockTable, showDelete, comment, showValidationMessage, isValidationInvalid, deleteHeaderText,
			showDocumentationDialog, currentFilterFieldName, currentFilterFieldType, overlayFunctionalityList,
			frozenColumns, allColumnWidths, tableKey, currentSortMeta, setFrozenColumns, toggleOverlay, onClickFilterFieldHelpIcon,
			onReload, onSort, onPage, onRowClick, onFilter, formatDate, onUpdate, requeueSelectedEntries, getBaseColumnWidth,
			resetColumnWidths, resetTableSettings, openMultiRequeueMenu, getExportsForToolTip, formatValue, showSaveSearch, openSelectSearch,
			clearSearch, documentList, columnResize,
		}
	},
});
</script>
<style lang="scss" scoped>

::v-deep(table.p-datatable-table) {
	width: unset !important;
}

// Table header
.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
  background-color: var(--dex-primary-light-color);
  padding: 0.6rem 0.6rem 0.2rem 0.6rem;
  border-bottom-color: var(--dex-primary-light-color);
  color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr td) {
	padding: 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-filter-column) {
	padding:0 0 0.2rem 0.2rem;
	color: var(--filter-field-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-filter-column .p-column-filter-menu-button, .p-column-filter-clear-button) {
	color: var(--filter-field-color);
}

// @todo Hover sortable icon have to be white!

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
  color: var(--dex-text-color);

  &:hover {
    color: var(--dex-text-color);
  }
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-dropdown.filter-field) {
  margin-left: 0.4rem;
  padding: 0.4rem;
  background-color: transparent;
  color: var(--dex-text-color);
  border-radius: 0;
  border-color: var(--filter-field-color);

  &:focus {
    border-color: rgba(255, 255, 255, 0.8);
  }

  .p-dropdown-label {
    padding: 0 1rem 0 0.4rem;
    color: var(--filter-field-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
  padding: 0.4rem;
  background-color: transparent;
  color: var(--dex-text-color);
  border-radius: 0;
  border-color: var(--dex-text-color);

  &:focus {
    border-color: rgba(255, 255, 255, 0.8);
  }
}

::placeholder {
	color: var(--filter-field-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-tbody tr td) {
	padding: 0.6rem;
	overflow: hidden;
}

::v-deep(.not-resizable-column span.p-column-resizer) {
	cursor: auto;
}

.status-badge {
	font-weight: bold;
	&.initial {
		border-color: var(--queuestate-initial-primary);
		color: var(--queuestate-initial-darker);
	}

	&.work {
		border-color: var(--queuestate-work-primary);
		color: var(--queuestate-work-darker);
	}

	&.waiting {
		border-color: var(--queuestate-waiting-primary);
		color: var(--queuestate-waiting-darker);
	}

	&.error {
		border-color: var(--queuestate-error-primary);
		color: var(--queuestate-error-darker);
	}

	&.suspend {
		border-color: var(--queuestate-suspend-primary);
		color: var(--queuestate-suspend-darker);
	}

	&.suspend-marked {
		border-color: var(--queuestate-suspend-darker);
		color: var(--queuestate-suspend-darker);
	}

	&.clarification {
		border-color: var(--queuestate-clarification-primary);
		color: var(--queuestate-clarification-darker);
	}

	&.finished {
		border-color: var(--queuestate-finished-primary);
		color: var(--queuestate-finished-darker);
	}
}

::v-deep(button.p-column-filter-menu-button.p-link) {
	display: none;
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(.p-column-filter-row .p-column-filter-element) {
	width: unset;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

.frozenIcon {
	color: var(--dex-text-color);
	position: absolute;

	&:hover {
		color: var(--text-color);
		opacity: 0.75;
	}
}

</style>

